import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { api } from "@/lib/api-client";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { IoIosWarning } from "react-icons/io";
import { LuCopySlash, LuPuzzle, LuTimerOff } from "react-icons/lu";
import { useCalendarStore } from "../../store/useCalendarStore";
import { LimeAutocomplete } from "@/Components/NextBase/LimeAutocomplete";
import {
  addToast,
  AutocompleteItem,
  AutocompleteSection,
  Divider,
  Tab,
  Tabs,
  Tooltip,
} from "@heroui/react";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { CopySlash, TimerOff } from "lucide-react";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { cn } from "@/utils";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { i18n } from "@lingui/core";
import { useClientFeature } from "@/hooks/useClientFeature";
import { LimeTimeRangeInput } from "@/Components/NextBase/LimeTimeRangeInput";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { parseDate } from "@internationalized/date";
import { formatSelectedTime, safeParseTime } from "@/utils/date-time-utils";
import { toCalendarDate } from "./calendarDate";
import { computeRepeatCountFromUntil } from "./recurrence";

const PrescheduledAppointmentModal = ({ onClose }) => {
  const { isFeatureEnabled } = useClientFeature();

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const {
    mutateAsync: createPrescheduled,
    isLoading: isPostPrescheduledLoading,
  } = api.appointments.usePostPrescheduled();

  const {
    services,
    users,
    resources,
    lastSelectedUserId,
    lastSelectedTime,
    lastSelectedDate,
    selectedLocationId,
    lastSelectedResourceId,
    newPreScheduledAppointmentData,
    setNewPreScheduledAppointmentData,
    calendarData,
    serviceTags,
  } = useCalendarStore((state) => ({
    services: state.services,
    users: state.users,
    resources: state.resources,
    lastSelectedUserId: state.lastSelectedUserId,
    lastSelectedTime: state.lastSelectedTime,
    lastSelectedDate: state.lastSelectedDate,
    selectedLocationId: state.selectedLocationId,
    lastSelectedResourceId: state.lastSelectedResourceId,
    newPreScheduledAppointmentData: state.newPreScheduledAppointmentData,
    calendarData: state.calendarData,
    serviceTags: state.serviceTags,
    setNewPreScheduledAppointmentData: state.setNewPreScheduledAppointmentData,
  }));

  const isEndInNextDay = dayjs(
    newPreScheduledAppointmentData?.endTime,
    "HH:mm",
  ).isSameOrBefore(dayjs(newPreScheduledAppointmentData?.startTime, "HH:mm"));

  const getDurationMinutes = (startTime, endTime) =>
    dayjs(endTime, "HH:mm")
      .add(isEndInNextDay ? 1 : 0, "day")
      .diff(dayjs(startTime, "HH:mm"), "minute");

  const { data: validOptions } = api.appointments.usePostReturnValidOptions(
    {
      selectedServices: newPreScheduledAppointmentData?.selectedServiceId
        ? [
            {
              resources: newPreScheduledAppointmentData?.resources,
              users: newPreScheduledAppointmentData?.selectedUserId
                ? [newPreScheduledAppointmentData?.selectedUserId]
                : [],
              blockAfterMins: 0,
              blockBeforeMins: 0,
              duration:
                getDurationMinutes(
                  newPreScheduledAppointmentData?.startTime,
                  newPreScheduledAppointmentData?.endTime,
                ) || 60,
              serviceId: newPreScheduledAppointmentData?.selectedServiceId || 0,
            },
          ]
        : [
            {
              serviceId: services[0]?.serviceId || 0,
              users: newPreScheduledAppointmentData?.selectedUserId
                ? [newPreScheduledAppointmentData?.selectedUserId]
                : [],
              resources: newPreScheduledAppointmentData?.resources || [],
              duration: 60,
            },
          ],
      selectedServiceIndex: 0,
      locationId: selectedLocationId,
      startTime: `${dayjs(lastSelectedDate).format("YYYY-MM-DD")}T${
        newPreScheduledAppointmentData?.startTime
      }`,
      enabled: newPreScheduledAppointmentData,
    },
    (!!permissions.show_appointment_warnings ||
      !!permissions.restrict_appointment_overlaps ||
      !!permissions.restrict_appointment_services ||
      !!permissions.restrict_appointment_to_shifts) &&
      !!newPreScheduledAppointmentData?.startTime,
  );

  useEffect(() => {
    setNewPreScheduledAppointmentData({
      ...newPreScheduledAppointmentData,
      startTime: formatSelectedTime(lastSelectedTime),
      endTime: null,
      numberOfPeople: 1,
      selectedUserId: lastSelectedUserId || null,
      isRepeating: false,
      repeatInterval: 1,
      repeatType: "week",
      repeatEndType: "date",
      repeatDuration: 1,
      repeatUntilDate: dayjs(lastSelectedDate)
        .add(1, "week")
        .format("YYYY-MM-DD"),
      isFixed: false,
      resources: lastSelectedResourceId ? [lastSelectedResourceId] : [],
      allowDuplicateCustomerOnAppointment:
        calendarData.defaultAllowDuplicateCustomerOnAppointment,
      showOnFormPreScheduled: true,
    });
  }, []);

  const submitData = async () => {
    if (!newPreScheduledAppointmentData) return;

    const duration = getDurationMinutes(
      newPreScheduledAppointmentData.startTime,
      newPreScheduledAppointmentData.endTime,
    );

    const repeatCount =
      newPreScheduledAppointmentData.repeatEndType === "date"
        ? computeRepeatCountFromUntil({
            startDate: lastSelectedDate,
            untilDate: newPreScheduledAppointmentData.repeatUntilDate,
            interval: newPreScheduledAppointmentData.repeatInterval,
            type: newPreScheduledAppointmentData.repeatType,
          })
        : newPreScheduledAppointmentData.repeatDuration;

    try {
      await createPrescheduled({
        locationId: selectedLocationId,
        users: newPreScheduledAppointmentData.selectedUserId
          ? [newPreScheduledAppointmentData.selectedUserId]
          : [],
        resources: newPreScheduledAppointmentData.resources || [],
        repeat: newPreScheduledAppointmentData.isRepeating
          ? {
              count: repeatCount,
              interval: newPreScheduledAppointmentData.repeatInterval,
              type: newPreScheduledAppointmentData.repeatType,
              isFixed: newPreScheduledAppointmentData.isFixed,
              noEndDate:
                newPreScheduledAppointmentData.isFixed &&
                newPreScheduledAppointmentData.repeatEndType === "none",
              untilDate:
                newPreScheduledAppointmentData.repeatEndType === "date" &&
                newPreScheduledAppointmentData.repeatUntilDate
                  ? dayjs(
                      newPreScheduledAppointmentData.repeatUntilDate,
                    ).format("YYYY-MM-DD")
                  : undefined,
            }
          : undefined,
        numberOfPeople: newPreScheduledAppointmentData.numberOfPeople,
        startTime: `${dayjs(lastSelectedDate).format("YYYY-MM-DD")}T${
          newPreScheduledAppointmentData.startTime
        }`,
        serviceId: newPreScheduledAppointmentData.selectedServiceId,
        duration,
        throwIfRestricted: true,
        allowDuplicateCustomerOnAppointment:
          newPreScheduledAppointmentData.allowDuplicateCustomerOnAppointment,
        showOnFormPreScheduled:
          newPreScheduledAppointmentData.showOnFormPreScheduled,
      });

      addToast({
        title: t`Termin uspešno ustvarjen`,
        color: "success",
      });

      onClose();
    } catch (e) {
      addToast({
        title: t`Napaka pri ustvarjanju termina`,
        color: "danger",
      });
    }
  };

  const selectedService = React.useMemo(
    () =>
      services.find(
        (s) =>
          s.serviceId === newPreScheduledAppointmentData?.selectedServiceId,
      ),
    [services, newPreScheduledAppointmentData],
  );

  const servicesWithWarnings = React.useMemo(
    () =>
      services
        .filter(
          (s) =>
            s.permission &&
            !s.isDeleted &&
            (!validOptions ||
              validOptions.availableServices.find(
                (ser) => s.serviceId === ser.serviceId && ser.isValid,
              )),
        )
        .toSorted((a, b) => {
          const aTagOrder =
            serviceTags.find((t) => t.tagId === a.tagId)?.sortOrder || 99;
          const bTagOrder =
            serviceTags.find((t) => t.tagId === b.tagId)?.sortOrder || 99;

          if (aTagOrder !== bTagOrder) return aTagOrder - bTagOrder;

          const aServiceOrder = a.sortOrder;
          const bServiceOrder = b.sortOrder;

          return aServiceOrder - bServiceOrder;
        })
        .map((service) => {
          const availableServiceDetails = validOptions?.availableServices?.find(
            (s) => s.serviceId === service.serviceId,
          );

          return {
            ...service,
            id: service.serviceId,
            value: service.serviceId.toString(),
            label: service.name,
            ...availableServiceDetails,
            isValid: availableServiceDetails
              ? availableServiceDetails.isValid
              : true,
            tagId: serviceTags.find((t) => t.tagId === service.tagId)?.tagId,
            tagName: serviceTags.find((t) => t.tagId === service.tagId)
              ?.tagName,
          };
        }),
    [services, serviceTags, validOptions],
  );

  const usersWithWarnings = React.useMemo(
    () =>
      users.flatMap((user) => {
        if (
          validOptions &&
          !validOptions.availableUsers.find(
            (usr) => user.userId === usr.userId && usr.isValid,
          )
        )
          return [];

        const firstName = user.name?.toUpperCase;
        const lastName = user.lastName?.toUpperCase;

        const availableUserDetails = validOptions?.availableUsers?.find(
          (u) => u.userId === user.userId,
        );

        return [
          {
            id: user.userId,
            value: user.userId.toString(),
            fullName: `${user.name} ${user.lastName}`,
            label: `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`,
            ...availableUserDetails,
            isValid: availableUserDetails ? availableUserDetails.isValid : true,
          },
        ];
      }),
    [users, validOptions],
  );

  const resourcesWithWarnings = React.useMemo(
    () =>
      resources.flatMap((r) => {
        if (
          validOptions &&
          !validOptions.availableResources.find(
            (res) => r.id === res.resourceId && res.isValid,
          )
        )
          return [];

        const availableResourceDetails = validOptions?.availableResources?.find(
          (res) => res.resourceId === r.id,
        );

        return [
          {
            id: r.id,
            value: r.id.toString(),
            label: r.label,
            ...availableResourceDetails,
            isValid: availableResourceDetails
              ? availableResourceDetails.isValid
              : true,
          },
        ];
      }),
    [resources, validOptions],
  );

  return (
    <div>
      <div className="flex flex-col gap-4">
        <p className="font-medium">
          {dayjs(lastSelectedDate).toDate().toLocaleDateString(i18n.locale)}
        </p>
        <LimeAutocomplete
          isVirtualized={false} // TODO: set to true after bugs are fixed (https://github.com/heroui-inc/heroui/issues/4494) , (https://github.com/heroui-inc/heroui/issues/4568)
          selectedKey={selectedService?.serviceId?.toString()}
          size={"sm"}
          placeholder={t`Storitev še ni izbrana`}
          label={t`Storitev`}
          variant="faded"
          data-identifier={"appointment-service-combobox"}
          listBoxProps={{
            itemClasses: {
              base: "h-fit w-fit",
            },
          }}
          scrollShadowProps={{
            isEnabled: false,
          }}
          onSelectionChange={(key) => {
            const val = Number(key);

            const selectedService = services.find((s) => s.serviceId === val);

            if (!selectedService) return;

            setNewPreScheduledAppointmentData({
              ...newPreScheduledAppointmentData,
              selectedServiceId: val,
              endTime: dayjs(
                `1970-01-01T${newPreScheduledAppointmentData.startTime}`,
              )
                .add(selectedService.duration, "minutes")
                .format("HH:mm"),
            });
          }}
        >
          {serviceTags
            ?.sort((a, b) => a.sortOrder - b.sortOrder)
            .map((st) => {
              const services =
                st.tagId === -1
                  ? servicesWithWarnings.filter((s) => !s.tagId)
                  : servicesWithWarnings.filter((s) => s.tagId === st.tagId);

              if (services?.length === 0 && st.tagId !== -1) return null;

              return (
                <AutocompleteSection
                  classNames={{
                    heading:
                      "flex w-full sticky top-1 z-20 py-1.5 px-2 bg-default-100 rounded-small",
                  }}
                  showDivider
                  title={st.tagName}
                >
                  {services?.map((s) => (
                    <AutocompleteItem
                      key={s.id.toString()}
                      textValue={s.label}
                      endContent={
                        <div className="flex items-center gap-2">
                          {s.userOverlapWarning ? (
                            <Tooltip
                              content={t`Zaposleni je zaseden`}
                              showArrow
                            >
                              <div>
                                <LuCopySlash color="var(--error)"></LuCopySlash>
                              </div>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                          {s.resourceOverlapWarning ? (
                            <Tooltip
                              content={t`Sredstvo je zasedeno`}
                              showArrow
                            >
                              <div>
                                <LuCopySlash color="var(--error)"></LuCopySlash>
                              </div>
                            </Tooltip>
                          ) : (
                            ""
                          )}

                          {s.userShiftWarning ? (
                            <Tooltip
                              content={t`Zaposleni nima urnika`}
                              showArrow
                            >
                              <div>
                                <LuTimerOff color="var(--error)"></LuTimerOff>
                              </div>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                          {s.resourceShiftWarning ? (
                            <Tooltip
                              content={t`Sredstvo nima urnika`}
                              showArrow
                            >
                              <div>
                                <LuTimerOff color="var(--error)"></LuTimerOff>
                              </div>
                            </Tooltip>
                          ) : (
                            ""
                          )}

                          {s.userRelationWarning ? (
                            <Tooltip
                              content={t`Zaposleni ne izvaja te storitve`}
                              showArrow
                            >
                              <div>
                                <IoIosWarning color="var(--error)"></IoIosWarning>
                              </div>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                          {s.resourceRelationWarning ? (
                            <Tooltip
                              content={t`Sredstvo ne izvaja te storitve`}
                              showArrow
                            >
                              <div>
                                <IoIosWarning color="var(--error)"></IoIosWarning>
                              </div>
                            </Tooltip>
                          ) : (
                            ""
                          )}

                          {s.combinationWarning ? (
                            <Tooltip
                              content={t`Storitev ni na voljo v kombinaciji z ostalimi izbranimi storitvami.`}
                              showArrow
                            >
                              <div>
                                <LuPuzzle color="var(--error)"></LuPuzzle>
                              </div>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                        </div>
                      }
                    >
                      {s.label}
                    </AutocompleteItem>
                  ))}
                </AutocompleteSection>
              );
            })}
        </LimeAutocomplete>
        {/**
         * user select
         */}
        <LimeSelect
          data-identifier="appointment-user-combobox"
          selectionMode={"single"}
          selectedKeys={
            newPreScheduledAppointmentData?.selectedUserId
              ? [newPreScheduledAppointmentData?.selectedUserId?.toString()]
              : []
          }
          onSingleItemChange={(val) => {
            setNewPreScheduledAppointmentData({
              ...newPreScheduledAppointmentData,
              selectedUserId: val ? Number(val) : null,
            });
          }}
          disallowEmptySelection={false}
          items={
            usersWithWarnings?.map((u) => {
              const userServiceWarning = u.userRelationWarning ? (
                <CopySlash size={14} color="var(--error)" />
              ) : undefined;
              const userScheduleWarning = u.userShiftWarning ? (
                <TimerOff size={14} color="var(--error)" />
              ) : undefined;
              const userAppointmentOverlapWarning = u.userOverlapWarning ? (
                <CopySlash size={14} color="var(--error)" />
              ) : undefined;

              return {
                key: u.id.toString(),
                label: u.fullName,
                endContent: (
                  <>
                    {userServiceWarning}
                    {userScheduleWarning}
                    {userAppointmentOverlapWarning}
                  </>
                ),
              };
            }) ?? []
          }
          label={t`Zaposleni`}
          size="sm"
        />

        {isFeatureEnabled("resources") && (
          <LimeSelect
            data-identifier="appointment-resource-combobox"
            selectionMode={"multiple"}
            selectedKeys={
              newPreScheduledAppointmentData?.resources?.map(String) || []
            }
            onMultipleItemChange={(vals) => {
              setNewPreScheduledAppointmentData({
                ...newPreScheduledAppointmentData,
                resources: vals.map(Number),
              });
            }}
            disallowEmptySelection={false}
            items={
              resourcesWithWarnings?.map((r) => {
                const relationWarning = r.relationWarning ? (
                  <CopySlash size={14} color="var(--error)" />
                ) : undefined;
                const shiftWarning = r.shiftWarning ? (
                  <TimerOff size={14} color="var(--error)" />
                ) : undefined;

                return {
                  key: r.id.toString(),
                  label: r.label,
                  endContent: (
                    <>
                      {relationWarning}
                      {shiftWarning}
                    </>
                  ),
                };
              }) ?? []
            }
            label={t`Sredstva`}
            size="sm"
          />
        )}

        <LimeTimeRangeInput
          fromValue={
            newPreScheduledAppointmentData?.startTime
              ? safeParseTime(
                  newPreScheduledAppointmentData.startTime,
                  "PrescheduledAppointmentModal.safeParseTime",
                )
              : null
          }
          toValue={
            newPreScheduledAppointmentData?.endTime
              ? safeParseTime(
                  newPreScheduledAppointmentData.endTime,
                  "PrescheduledAppointmentModal.safeParseTime",
                )
              : null
          }
          onFromChange={(value) => {
            if (!value) return;

            const from = dayjs(value.toString(), "HH:mm:ss");
            const newStartTime = from.format("HH:mm");

            const { startTime, endTime } = newPreScheduledAppointmentData;
            const shiftedEndTime = endTime
              ? from
                  .add(getDurationMinutes(startTime, endTime), "minute")
                  .format("HH:mm")
              : null;

            setNewPreScheduledAppointmentData({
              ...newPreScheduledAppointmentData,
              startTime: newStartTime,
              endTime: shiftedEndTime,
            });
          }}
          onToChange={(value) => {
            if (!value) return;

            setNewPreScheduledAppointmentData({
              ...newPreScheduledAppointmentData,
              endTime: dayjs(value.toString(), "HH:mm:ss").format("HH:mm"),
            });
          }}
          isToDisabled={!!permissions.restrict_custom_duration}
          allowOvernight
        />

        <LimeNumberInput
          data-identifier={"appointment-number-of-people-input"}
          label={t`Število udeležencev`}
          minValue={1}
          maxValue={99999}
          step={1}
          value={newPreScheduledAppointmentData?.numberOfPeople}
          onValueChange={(value) => {
            setNewPreScheduledAppointmentData({
              ...newPreScheduledAppointmentData,
              numberOfPeople: value,
            });
          }}
        />

        <LimeSwitch
          title={t`Dovoli podvojene stranke na terminu`}
          checked={
            newPreScheduledAppointmentData?.allowDuplicateCustomerOnAppointment
          }
          onValueChange={(value) => {
            setNewPreScheduledAppointmentData({
              ...newPreScheduledAppointmentData,
              allowDuplicateCustomerOnAppointment: value,
            });
          }}
          isCompact
        />
      </div>

      <Divider className="my-4" />

      <LimeSwitch
        data-identifier={"repeating-appointment-switch"}
        title={t`Ponavljajoč termin`}
        checked={newPreScheduledAppointmentData?.isRepeating}
        onChange={(e) => {
          setNewPreScheduledAppointmentData({
            ...newPreScheduledAppointmentData,
            isRepeating: e.currentTarget.checked,
          });
        }}
        isCompact
        className={cn("transition-all duration-150", {
          "mb-4": newPreScheduledAppointmentData?.isRepeating,
        })}
      />

      <LimeCollapse isOpen={newPreScheduledAppointmentData?.isRepeating}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="mr-4 text-sm whitespace-pre">
              <Trans>Ponovi vsak</Trans>
            </p>

            <LimeNumberInput
              data-identifier={"repeat-interval"}
              placeholder={"1"}
              size="sm"
              value={newPreScheduledAppointmentData?.repeatInterval}
              onValueChange={(value) => {
                setNewPreScheduledAppointmentData({
                  ...newPreScheduledAppointmentData,
                  repeatInterval: value,
                });
              }}
              minValue={1}
              step={1}
              className="mr-2"
            />

            <LimeSelect
              data-identifier={"repeat-type-select"}
              selectedKeys={
                newPreScheduledAppointmentData?.repeatType
                  ? [newPreScheduledAppointmentData?.repeatType]
                  : []
              }
              onSingleItemChange={(val) => {
                setNewPreScheduledAppointmentData({
                  ...newPreScheduledAppointmentData,
                  repeatType: val,
                });
              }}
              variant="flat"
              items={[
                { label: t`Dan`, key: "day" },
                { label: t`Teden`, key: "week" },
                { label: t`Mesec`, key: "month" },
              ]}
              size="md"
              defaultValue={"week"}
            />
          </div>
          <Tabs
            aria-label={t`Način zaključka ponavljanja`}
            size="sm"
            fullWidth
            selectedKey={
              newPreScheduledAppointmentData?.repeatEndType ?? "date"
            }
            onSelectionChange={(key) => {
              const nextData = {
                ...newPreScheduledAppointmentData,
                repeatEndType: key,
              };

              if (
                key === "date" &&
                !newPreScheduledAppointmentData?.repeatUntilDate
              ) {
                const steps = Math.max(
                  1,
                  (newPreScheduledAppointmentData?.repeatDuration || 1) - 1,
                );

                nextData.repeatUntilDate = dayjs(lastSelectedDate)
                  .add(
                    steps *
                      (newPreScheduledAppointmentData?.repeatInterval || 1),
                    newPreScheduledAppointmentData?.repeatType || "week",
                  )
                  .format("YYYY-MM-DD");
              }

              setNewPreScheduledAppointmentData(nextData);
            }}
          >
            <Tab
              key="date"
              data-identifier={"repeat-end-date-tab"}
              title={t`Ponavljaj do datuma`}
            >
              <div className="flex flex-col gap-2">
                <LimeDatePicker
                  data-identifier={"repeat-until-date"}
                  label={t`Ponavljaj do`}
                  size="sm"
                  granularity="day"
                  value={
                    newPreScheduledAppointmentData?.repeatUntilDate
                      ? parseDate(
                          newPreScheduledAppointmentData.repeatUntilDate,
                        )
                      : null
                  }
                  minValue={toCalendarDate(lastSelectedDate)}
                  onChange={(value) => {
                    setNewPreScheduledAppointmentData({
                      ...newPreScheduledAppointmentData,
                      repeatUntilDate: value ? value.toString() : null,
                    });
                  }}
                />
                {newPreScheduledAppointmentData?.repeatUntilDate &&
                  (() => {
                    const count = computeRepeatCountFromUntil({
                      startDate: lastSelectedDate,
                      untilDate: newPreScheduledAppointmentData.repeatUntilDate,
                      interval: newPreScheduledAppointmentData.repeatInterval,
                      type: newPreScheduledAppointmentData.repeatType,
                    });

                    const lastDate = dayjs(lastSelectedDate)
                      .add(
                        (count - 1) *
                          (newPreScheduledAppointmentData.repeatInterval || 1),
                        newPreScheduledAppointmentData.repeatType,
                      )
                      .toDate()
                      .toLocaleDateString(i18n.locale);

                    return (
                      <p className="text-tiny text-default-500">
                        <Trans>
                          Ustvari {count} terminov, zadnji {lastDate}
                        </Trans>
                      </p>
                    );
                  })()}
              </div>
            </Tab>
            <Tab
              key="count"
              data-identifier={"repeat-end-count-tab"}
              title={t`Število ponavljanj`}
            >
              <LimeNumberInput
                data-identifier={"repeat-duration"}
                label={t`Število ponavljanj`}
                placeholder={"1"}
                value={newPreScheduledAppointmentData?.repeatDuration}
                onValueChange={(value) => {
                  setNewPreScheduledAppointmentData({
                    ...newPreScheduledAppointmentData,
                    repeatDuration: value,
                  });
                }}
                minValue={1}
                step={1}
              />
            </Tab>
            {newPreScheduledAppointmentData?.isFixed && (
              <Tab
                key="none"
                data-identifier={"repeat-end-none-tab"}
                title={t`Brez končnega datuma`}
              >
                <p className="text-tiny text-default-500">
                  <Trans>
                    Termini bodo ustvarjeni leto dni vnaprej in sproti dodani.
                  </Trans>
                </p>
              </Tab>
            )}
          </Tabs>
          {isFeatureEnabled("fixedAppointments") && (
            <LimeSwitch
              data-identifier={"fixed-appointment-switch"}
              title={t`Redni termin`}
              checked={newPreScheduledAppointmentData?.isFixed}
              onChange={(e) => {
                const isFixed = e.currentTarget.checked;
                setNewPreScheduledAppointmentData({
                  ...newPreScheduledAppointmentData,
                  isFixed,
                  // Only a fixed appointment can repeat without an end date.
                  repeatEndType:
                    !isFixed &&
                    newPreScheduledAppointmentData?.repeatEndType === "none"
                      ? "count"
                      : newPreScheduledAppointmentData?.repeatEndType,
                });
              }}
              isCompact
            />
          )}
        </div>
      </LimeCollapse>

      <Divider className="my-4" />

      <LimeSwitch
        title={t`Prikaži na spletni formi`}
        checked={newPreScheduledAppointmentData?.showOnFormPreScheduled}
        onValueChange={(value) => {
          setNewPreScheduledAppointmentData({
            ...newPreScheduledAppointmentData,
            showOnFormPreScheduled: value,
          });
        }}
        isCompact
      />

      <button
        id={"submit-scheduled-appointment-button"}
        onClick={submitData}
        style={{
          display: "none",
        }}
        disabled={isPostPrescheduledLoading}
      >
        <Trans>Potrdi</Trans>
      </button>
    </div>
  );
};

export default PrescheduledAppointmentModal;
